/**
 * Anonymous tenant_user_id provisioning utility.
 *
 * Guarantees that every visitor reaching the widget without a
 * host-injected `window.FIINDER_TENANT_USER_ID` still carries a
 * stable opaque identifier across browser sessions, so analytics
 * cover anonymous traffic too.
 *
 * The id follows the form `anon-<uuid-v4>`, persists in
 * `localStorage["fiinder_anonymous_tenant_user_id"]`, and is
 * provisioned lazily on first call. The id is short-lived only
 * when the runtime cannot persist (private mode, ITP) — otherwise
 * it survives reloads and host-runtime identity changes that
 * sweep the storage.
 */

const STORAGE_KEY = "fiinder_anonymous_tenant_user_id";
const ANONYMOUS_PREFIX = "anon-";
const VALIDATION_REGEX = /^[A-Za-z0-9_\-:.@]*$/;
const MAX_LENGTH = 128;

let cachedValue: string | undefined;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function isValidAnonymousId(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (!value.startsWith(ANONYMOUS_PREFIX)) return false;
  if (value.length > MAX_LENGTH) return false;
  if (!VALIDATION_REGEX.test(value)) return false;
  return true;
}

function generateUuidLike(): string {
  try {
    const native = globalThis.crypto?.randomUUID?.();
    if (typeof native === "string" && native.length > 0) {
      return native;
    }
  } catch {
    // Fall through to the Math-based fallback below.
  }

  // Fallback for runtimes lacking a reliable `crypto.randomUUID`.
  // Produces a UUID-like 8-4-4-4-12 string from `Math.random` plus
  // a millisecond timestamp. Output still matches the validation
  // regex; uniqueness is statistical, sufficient for anonymous
  // session correlation (non-cryptographic use).
  const seedFromTime = Date.now().toString(16).padStart(12, "0").slice(-12);
  const rand = (length: number) =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");
  return `${rand(8)}-${rand(4)}-${rand(4)}-${rand(4)}-${seedFromTime}`;
}

function generateAnonymousId(): string {
  return `${ANONYMOUS_PREFIX}${generateUuidLike()}`;
}

function safeReadFromStorage(): string | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? undefined : raw;
  } catch {
    console.debug(
      "[anonymous-tenant-user-id] localStorage read failed; treating as absent",
    );
    return undefined;
  }
}

function safePersistToStorage(value: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage may be unavailable (private mode, quota, ITP). The
    // cached value still keeps the id usable for the rest of the
    // session — we accept losing cross-session stability.
    console.debug(
      "[anonymous-tenant-user-id] localStorage write failed; using in-memory only",
    );
  }
}

/**
 * Returns a stable anonymous `tenant_user_id` for the current
 * browser session. Lazy: on first call generates a new id (or
 * recovers an existing one from `localStorage`); subsequent calls
 * return the cached value.
 *
 * Returns `undefined` in SSR environments (`typeof window ===
 * "undefined"`), so callers running on the server can short-circuit
 * without touching browser-only APIs.
 *
 * Failures in `localStorage` (private mode, quota) degrade to an
 * in-memory id that lives for the lifetime of the module — the id
 * remains usable for the session but does not survive a reload.
 */
export function getOrGenerateAnonymousTenantUserId(): string | undefined {
  if (!isBrowser()) return undefined;
  if (cachedValue !== undefined) return cachedValue;

  const fromStorage = safeReadFromStorage();
  if (isValidAnonymousId(fromStorage)) {
    cachedValue = fromStorage;
    console.debug(
      "[anonymous-tenant-user-id] recovered persisted id from storage",
    );
    return cachedValue;
  }

  if (fromStorage !== undefined) {
    console.debug(
      "[anonymous-tenant-user-id] discarded corrupted persisted value",
    );
  }

  const fresh = generateAnonymousId();
  cachedValue = fresh;
  safePersistToStorage(fresh);
  console.debug("[anonymous-tenant-user-id] generated new anonymous id");

  return cachedValue;
}

/**
 * Clears the in-memory cache so the next call to
 * `getOrGenerateAnonymousTenantUserId` re-reads `localStorage` or
 * generates a fresh value. Used when the host clears the visitor
 * identity at runtime — the sweep wipes the persisted id and the
 * cache must follow.
 */
export function resetAnonymousCache(): void {
  cachedValue = undefined;
}
